import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AppProvider, useApp } from "@/context/AppContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import MasterEnabler from "@/pages/MasterEnabler";
import FamilyTree from "@/pages/FamilyTree";
import { Benefits, Currency, Holdings } from "@/pages/Modules";
import Recipients from "@/pages/Recipients";
import Alerts from "@/pages/Alerts";
import Activity from "@/pages/Activity";
import Runners from "@/pages/Runners";
import ForcePassword from "@/pages/ForcePassword";
import SyncBackup from "@/pages/SyncBackup";
import Privacy from "@/pages/Privacy";
import StoreListings from "@/pages/StoreListings";
import ReviewsAdmin from "@/pages/ReviewsAdmin";
import StoreScreenshots from "@/pages/StoreScreenshots";
import SetupPassword from "@/pages/SetupPassword";
import Settings from "@/pages/Settings";
import DablConsole from "@/pages/DablConsole";
import MonthlyLogs from "@/pages/MonthlyLogs";
import TeamDirectory from "@/pages/TeamDirectory";
import MyTeam from "@/pages/MyTeam";
import WorkRouting from "@/pages/WorkRouting";
import Security from "@/pages/Security";
import ReportsCompliance from "@/pages/ReportsCompliance";
import PublicVerify from "@/pages/PublicVerify";
import ClusterControl from "@/pages/ClusterControl";
import ResetPassword from "@/pages/ResetPassword";
import ChangeReview from "@/pages/ChangeReview";
import Account from "@/pages/Account";
import SupportLogin from "@/pages/SupportLogin";
import SupportConsole from "@/pages/SupportConsole";
import DemoCodes from "@/pages/DemoCodes";
import ClaimCode from "@/pages/ClaimCode";
import DemoLogin from "@/pages/DemoLogin";
import Audiences from "@/pages/Audiences";
import { Home, Blog, BlogPost, About, Newsletter, NewsletterIssue, NewsletterShare, Contact, Solutions, AllReviews, TermsPage, PrivacyPage, CookiesPage, BackupPage, EulaPage, CopyrightPage } from "@/pages/Marketing";
import Portal from "@/pages/Portal";
import WatchDemo from "@/pages/WatchDemo";
import SignAgreement from "@/pages/SignAgreement";
import PublicTree from "@/pages/PublicTree";
import MaintenancePage from "@/pages/MaintenancePage";
import LedgerPopout from "@/pages/LedgerPopout";
import LedgerTilePopout from "@/pages/LedgerTilePopout";
import MyPurchases from "@/pages/MyPurchases";
import FopSeo from "@/pages/FopSeo";
import { trackPage } from "@/lib/analytics";
import { useEffect } from "react";
import CookieConsent from "@/components/CookieConsent";
import PolicyAckPrompt from "@/components/PolicyAckPrompt";
import LanguageNudge from "@/components/LanguageNudge";
import { Toaster } from "sonner";

function PageTracker() {
  const { pathname } = useLocation();
  // Site analytics = public/marketing traffic only; skip authenticated in-app routes.
  useEffect(() => { if (!pathname.startsWith("/app")) trackPage(pathname); }, [pathname]);
  return null;
}

function Protected({ children, admin, navKey }) {
  const { token, loading, isAdmin, delegatedKeys = [], mustChangePassword, needsAgreement } = useApp();
  if (loading) return <div className="grid min-h-screen place-items-center opacity-60">Loading…</div>;
  if (!token) return <Navigate to="/login" replace />;
  if (mustChangePassword) return <ForcePassword />;
  if (needsAgreement) return <SignAgreement />;
  if (admin && !isAdmin && !(navKey && delegatedKeys.includes(navKey))) return <Layout><Navigate to="/app" replace /></Layout>;
  return <Layout>{children}</Layout>;
}

// Landing ("/"): normal runners go straight to their workspace; a privileged (admin/support)
// session is NOT silently forwarded into the app — it stays on the public site so a stale token
// can't drop you into the Admin dashboard unexpectedly (see PrivilegedSessionBanner).
function Landing() {
  const { token, user } = useApp();
  if (token && user && user.role === "user") return <Navigate to="/app" replace />;
  return <Home />;
}

// Always-visible notice when a privileged session is active while browsing public/demo pages,
// so the signed-in identity is never a surprise. Offers quick "Enter workspace" / "Sign out".
function PrivilegedSessionBanner() {
  const { user, logout } = useApp();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  if (!user || !["admin", "support"].includes(user.role)) return null;
  if (pathname.startsWith("/app") || pathname.startsWith("/ledger") || pathname === "/maintenance") return null;
  return (
    <div data-testid="priv-session-banner"
      className="fixed inset-x-0 top-0 z-[60] flex flex-wrap items-center gap-x-2 gap-y-1 bg-[var(--primary,#0f62fe)] px-4 py-2 text-sm text-white shadow-lg">
      <span className="font-semibold">Active {user.role === "admin" ? "Admin" : "Support"} session</span>
      <span className="opacity-90">— signed in as {user.name || user.username || user.email}. You're viewing the public site.</span>
      <div className="ms-auto flex items-center gap-2">
        <button data-testid="priv-enter-workspace" onClick={() => navigate("/app")}
          className="rounded-md bg-white/20 px-3 py-1 font-medium transition hover:bg-white/30">Enter workspace</button>
        <button data-testid="priv-sign-out" onClick={logout}
          className="rounded-md bg-black/25 px-3 py-1 font-medium transition hover:bg-black/35">Sign out</button>
      </div>
    </div>
  );
}

function Shell() {
  const { token } = useApp();
  return (
    <>
    <PrivilegedSessionBanner />
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/verify" element={<PublicVerify />} />
      <Route path="/solutions" element={<Solutions />} />
      <Route path="/reviews" element={<AllReviews />} />
      <Route path="/watch-demo" element={<WatchDemo />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/about" element={<About />} />
      <Route path="/newsletter" element={<Newsletter />} />
      <Route path="/newsletter/share" element={<NewsletterShare />} />
      <Route path="/portal/:token" element={<Portal />} />
      <Route path="/newsletter/:slug" element={<NewsletterIssue />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/cookies" element={<CookiesPage />} />
      <Route path="/backup" element={<BackupPage />} />
      <Route path="/eula" element={<EulaPage />} />
      <Route path="/copyright" element={<CopyrightPage />} />
      <Route path="/login" element={token ? <Navigate to="/app" replace /> : <Login />} />
      <Route path="/support-login" element={token ? <Navigate to="/app" replace /> : <SupportLogin />} />
      <Route path="/demo-login" element={token ? <Navigate to="/app" replace /> : <DemoLogin />} />
      <Route path="/claim-code" element={<ClaimCode />} />
      <Route path="/setup" element={<SetupPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/change-review" element={<ChangeReview />} />
      <Route path="/share/:token" element={<PublicTree />} />
      <Route path="/maintenance" element={<MaintenancePage />} />
      <Route path="/app" element={<Protected><Dashboard /></Protected>} />
      <Route path="/app/account" element={<Protected><Account /></Protected>} />
      <Route path="/app/purchases" element={<Protected><MyPurchases /></Protected>} />
      <Route path="/app/enabler" element={<Protected><MasterEnabler /></Protected>} />
      <Route path="/app/family" element={<Protected><FamilyTree /></Protected>} />
      <Route path="/app/benefits" element={<Protected><Benefits /></Protected>} />
      <Route path="/app/currency" element={<Protected><Currency /></Protected>} />
      <Route path="/app/holdings" element={<Protected><Holdings /></Protected>} />
      <Route path="/ledger/tile" element={<LedgerTilePopout />} />
      <Route path="/ledger/:app/:accountId" element={<LedgerPopout />} />
      <Route path="/app/recipients" element={<Protected><Recipients /></Protected>} />
      <Route path="/app/alerts" element={<Protected><Alerts /></Protected>} />
      <Route path="/app/activity" element={<Protected><Activity /></Protected>} />
      <Route path="/app/sync" element={<Protected><SyncBackup /></Protected>} />
      <Route path="/app/privacy" element={<Protected><Privacy /></Protected>} />
      <Route path="/app/runners" element={<Protected admin navKey="runners"><Runners /></Protected>} />
      <Route path="/app/support" element={<Protected admin navKey="support_console"><SupportConsole /></Protected>} />
      <Route path="/app/team" element={<Protected admin navKey="team"><TeamDirectory /></Protected>} />
      <Route path="/app/my-team" element={<Protected><MyTeam /></Protected>} />
      <Route path="/app/routing" element={<Protected><WorkRouting /></Protected>} />
      <Route path="/app/demo-codes" element={<Protected><DemoCodes /></Protected>} />
      <Route path="/app/audiences" element={<Protected admin navKey="audiences"><Audiences /></Protected>} />
      <Route path="/app/listings" element={<Protected admin navKey="store_listings"><StoreListings /></Protected>} />
      <Route path="/app/reviews" element={<Protected admin navKey="reviews"><ReviewsAdmin /></Protected>} />
      <Route path="/app/screenshots" element={<Protected admin navKey="screenshots"><StoreScreenshots /></Protected>} />
      <Route path="/app/settings" element={<Protected admin navKey="settings"><Settings /></Protected>} />
      <Route path="/app/dabl" element={<Protected admin navKey="dabl"><DablConsole /></Protected>} />
      <Route path="/app/monthly-logs" element={<Protected admin navKey="monthly_logs"><MonthlyLogs /></Protected>} />
      <Route path="/app/security" element={<Protected admin navKey="security"><Security /></Protected>} />
      <Route path="/app/reports-compliance" element={<Protected admin navKey="reports_compliance"><ReportsCompliance /></Protected>} />
      <Route path="/app/cluster" element={<Protected admin navKey="cluster"><ClusterControl /></Protected>} />
      <Route path="/app/fop-seo" element={<Protected admin navKey="fop_seo"><FopSeo /></Protected>} />
      <Route path="*" element={<Navigate to="/app" replace />} />
    </Routes>
    </>
  );
}

export default function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <PageTracker />
        <Shell />
        <CookieConsent />
        <PolicyAckPrompt />
        <LanguageNudge />
        <Toaster position="bottom-right" closeButton richColors />
      </BrowserRouter>
    </AppProvider>
  );
}
